import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BarChart3, DollarSign, FileText, TrendingUp, PieChart, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, ReferenceLine } from 'recharts';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { ProjectService } from '../services/projectService';
import { ClientService } from '../services/clientService';
import { ProfitAnalysisService } from '../services/profitAnalysisService';
import { useAuth } from '../contexts/AuthContext';
import ProfitFilters from './ProfitAnalysis/ProfitFilters';
import ProjectProfitTable from './ProfitAnalysis/ProjectProfitTable';
import LeadSourceAnalytics from './LeadSourceAnalytics';
import ClientRevenueAnalytics from './ClientRevenueAnalytics';
import './ManagerDashboard.css';
import './ProfitAnalysis/ProfitAnalysis.css';

const ADMIN_EMAIL = 'adam@ab-civil.com';

const ManagerDashboard = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [timeView, setTimeView] = useState('weekly'); // weekly, monthly, quarterly, yearly
  const [activeTab, setActiveTab] = useState('financial'); // financial, profit-analysis, lead-source, client-revenue
  const tooltipTimeout = useRef(null);
  const [chartColors, setChartColors] = useState({
    textPrimary: '#F9FAFB',
    textSecondary: '#D1D5DB',
    borderPrimary: '#4B5563',
  });

  // Profit Analysis state
  const [profitProjects, setProfitProjects] = useState([]);
  const [profitLoading, setProfitLoading] = useState(false);
  const [profitFilters, setProfitFilters] = useState(() => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    return {
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      templateTypes: [],
      clientIds: [],
      states: [],
      acreageRanges: [],
      billingStatuses: [],
      searchTerm: ''
    };
  });

  // Check if user has admin access for Profit Analysis tab
  const hasAdminAccess = user?.email === ADMIN_EMAIL;

  // Get computed CSS variables for chart colors
  const getChartColors = () => {
    const rootStyles = getComputedStyle(document.documentElement);
    return {
      textPrimary: rootStyles.getPropertyValue('--text-primary').trim() || '#F9FAFB',
      textSecondary: rootStyles.getPropertyValue('--text-secondary').trim() || '#D1D5DB',
      borderPrimary: rootStyles.getPropertyValue('--border-primary').trim() || '#4B5563',
    };
  };

  // Update chart colors when component mounts and when theme changes
  useEffect(() => {
    const updateColors = () => {
      setChartColors(getChartColors());
    };

    updateColors();

    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          updateColors();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Load profit analysis data when tab is selected (only for admin)
  useEffect(() => {
    if (activeTab === 'profit-analysis' && hasAdminAccess && profitProjects.length === 0) {
      loadProfitData();
    }
  }, [activeTab, hasAdminAccess]);

  const loadProfitData = async () => {
    try {
      setProfitLoading(true);
      const data = await ProfitAnalysisService.getProjectProfitData();
      setProfitProjects(data);
    } catch (err) {
      console.error('Error loading profit data:', err);
    } finally {
      setProfitLoading(false);
    }
  };

  // Filtered profit projects based on current filters
  const filteredProfitProjects = useMemo(() => {
    return ProfitAnalysisService.filterProjects(profitProjects, profitFilters);
  }, [profitProjects, profitFilters]);

  // Filter options derived from profit projects
  const profitFilterOptions = useMemo(() => {
    return {
      templateTypes: ProfitAnalysisService.getUniqueTemplateTypes(profitProjects),
      clients: ProfitAnalysisService.getUniqueClients(profitProjects),
      states: ProfitAnalysisService.getUniqueStates(profitProjects),
      billingStatuses: ProfitAnalysisService.getUniqueBillingStatuses(profitProjects)
    };
  }, [profitProjects]);

  // Handler for profit filter changes
  const handleProfitFilterChange = useCallback((newFilters) => {
    setProfitFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Handler for clearing profit filters
  const handleClearProfitFilters = useCallback(() => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    setProfitFilters({
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      templateTypes: [],
      clientIds: [],
      states: [],
      acreageRanges: [],
      billingStatuses: [],
      searchTerm: ''
    });
  }, []);

  // Handler for CSV export
  const handleProfitExport = useCallback(() => {
    ProfitAnalysisService.exportToCSV(filteredProfitProjects);
  }, [filteredProfitProjects]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [projectsData, clientsData] = await Promise.all([
        ProjectService.getAllProjects(),
        ClientService.getClientsWithAnalytics()
      ]);

      setProjects(projectsData);
      setClients(clientsData);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const backlogTotal = useMemo(() => {
    return projects
      .filter(p => {
        const billingStatus = p.billingStatus || 'None';
        return !['Paid', 'Voided', 'Invoiced'].includes(billingStatus);
      })
      .reduce((sum, p) => sum + (p.modelPrice || 0), 0);
  }, [projects]);

  const openInvoicesTotal = useMemo(() => {
    return projects
      .filter(p => p.billingStatus === 'Invoiced')
      .reduce((sum, p) => sum + (p.modelPrice || 0), 0);
  }, [projects]);

  const pendingInvoiceTotal = useMemo(() => {
    return projects
      .filter(p => {
        if (p.readyToInvoice !== 'Yes') return false;
        if (p.billingStatus === 'Invoiced' || p.billingStatus === 'Paid' || p.billingStatus === 'Voided') return false;
        return true;
      })
      .reduce((sum, p) => sum + (p.modelPrice || 0), 0);
  }, [projects]);

  // Helper function to process period data
  const processPeriodData = useCallback((periods) => {
    return periods.map(period => {
      // New contracts (based on initial acceptance date)
      const newContracts = projects.filter(p => {
        if (!p.initialAcceptance) return false;
        const acceptanceDate = new Date(p.initialAcceptance);
        return acceptanceDate >= period.start && acceptanceDate <= period.end;
      });
      const newBase = newContracts.filter(p => !p.rev || p.rev === 0 || p.rev === '0');
      const newRev = newContracts.filter(p => p.rev && p.rev !== 0 && p.rev !== '0');

      // Invoiced (based on invoiced date)
      const invoiced = projects.filter(p => {
        if (!p.invoicedDate) return false;
        const invoicedDate = new Date(p.invoicedDate);
        return invoicedDate >= period.start && invoicedDate <= period.end;
      });
      const invoicedBase = invoiced.filter(p => !p.rev || p.rev === 0 || p.rev === '0');
      const invoicedRev = invoiced.filter(p => p.rev && p.rev !== 0 && p.rev !== '0');

      // Paid (based on paid date)
      const paid = projects.filter(p => {
        if (!p.paidDate) return false;
        const paidDate = new Date(p.paidDate);
        return paidDate >= period.start && paidDate <= period.end;
      });
      const paidBase = paid.filter(p => !p.rev || p.rev === 0 || p.rev === '0');
      const paidRev = paid.filter(p => p.rev && p.rev !== 0 && p.rev !== '0');

      // Pending Invoice (ready_to_invoice = Yes, but not yet invoiced/paid/voided)
      // Use last_stage_change_date to determine which period it falls into
      const pendingInvoice = projects.filter(p => {
        if (p.readyToInvoice !== 'Yes') return false;
        if (p.billingStatus === 'Invoiced' || p.billingStatus === 'Paid' || p.billingStatus === 'Voided') return false;
        if (!p.lastStageChangeDate) return false;
        const stageDate = new Date(p.lastStageChangeDate);
        return stageDate >= period.start && stageDate <= period.end;
      });
      const pendingBase = pendingInvoice.filter(p => !p.rev || p.rev === 0 || p.rev === '0');
      const pendingRev = pendingInvoice.filter(p => p.rev && p.rev !== 0 && p.rev !== '0');

      return {
        period: period.label,
        newContractsBase: newBase.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        newContractsRev: newRev.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        invoicedBase: invoicedBase.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        invoicedRev: invoicedRev.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        pendingInvoiceBase: pendingBase.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        pendingInvoiceRev: pendingRev.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        paidBase: paidBase.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        paidRev: paidRev.reduce((sum, p) => sum + (p.modelPrice || 0), 0),
        newContractsBaseCount: newBase.length,
        newContractsRevCount: newRev.length,
        invoicedBaseCount: invoicedBase.length,
        invoicedRevCount: invoicedRev.length,
        pendingInvoiceBaseCount: pendingBase.length,
        pendingInvoiceRevCount: pendingRev.length,
        paidBaseCount: paidBase.length,
        paidRevCount: paidRev.length
      };
    });
  }, [projects]);

  const weeklyData = useMemo(() => {
    const today = new Date();
    const weeks = [];

    // Calculate the most recent Sunday
    const mostRecentSunday = new Date(today);
    const dayOfWeek = today.getDay();
    mostRecentSunday.setDate(today.getDate() - dayOfWeek);
    mostRecentSunday.setHours(0, 0, 0, 0);

    // Go back 7 weeks from the most recent Sunday to get 8 weeks total
    for (let i = 7; i >= 0; i--) {
      const weekStart = new Date(mostRecentSunday);
      weekStart.setDate(mostRecentSunday.getDate() - (i * 7));

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      weeks.push({
        start: weekStart,
        end: weekEnd,
        label: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }

    return processPeriodData(weeks);
  }, [processPeriodData]);

  const monthlyData = useMemo(() => {
    const today = new Date();
    const months = [];
    
    // Get last 10 months
    for (let i = 9; i >= 0; i--) {
      const monthStart = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() - i + 1, 0, 23, 59, 59, 999);
      
      months.push({
        start: monthStart,
        end: monthEnd,
        label: monthStart.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      });
    }
    
    return processPeriodData(months);
  }, [processPeriodData]);

  const quarterlyData = useMemo(() => {
    const today = new Date();
    const quarters = [];
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const currentYear = today.getFullYear();
    
    // Get last 10 quarters
    for (let i = 9; i >= 0; i--) {
      const quarterOffset = currentQuarter - i;
      const year = currentYear + Math.floor(quarterOffset / 4);
      const quarter = ((quarterOffset % 4) + 4) % 4;
      
      const quarterStart = new Date(year, quarter * 3, 1);
      const quarterEnd = new Date(year, quarter * 3 + 3, 0, 23, 59, 59, 999);
      
      quarters.push({
        start: quarterStart,
        end: quarterEnd,
        label: `Q${quarter + 1} ${year.toString().slice(-2)}`
      });
    }
    
    return processPeriodData(quarters);
  }, [processPeriodData]);

  const yearlyData = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const years = [];

    // Get last 6 years (to include 2021)
    for (let i = 5; i >= 0; i--) {
      const year = currentYear - i;
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);

      years.push({
        start: yearStart,
        end: yearEnd,
        label: year.toString()
      });
    }

    return processPeriodData(years);
  }, [processPeriodData]);

  const getChartData = () => {
    switch (timeView) {
      case 'monthly': return monthlyData;
      case 'quarterly': return quarterlyData;
      case 'yearly': return yearlyData;
      default: return weeklyData;
    }
  };

  const getGoalLineValue = () => {
    const annualGoal = 850000;
    switch (timeView) {
      case 'weekly':
        return annualGoal / 52; // $16,346 per week
      case 'monthly':
        return annualGoal / 12; // $70,833 per month
      case 'quarterly':
        return annualGoal / 4; // $212,500 per quarter
      case 'yearly':
        return annualGoal; // $850,000 per year
      default:
        return annualGoal / 52;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length && tooltipVisible) {
      const data = payload[0].payload;

      const newContractsTotal = (data.newContractsBase || 0) + (data.newContractsRev || 0);
      const invoicedTotal = (data.invoicedBase || 0) + (data.invoicedRev || 0);
      const pendingInvoiceTotal = (data.pendingInvoiceBase || 0) + (data.pendingInvoiceRev || 0);
      const paidTotal = (data.paidBase || 0) + (data.paidRev || 0);

      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          <div className="tooltip-section">
            {newContractsTotal > 0 && (
              <p className="tooltip-item new-contracts">
                <span className="tooltip-dot" style={{ backgroundColor: '#60a5fa' }}></span>
                New Contracts: {formatCurrency(newContractsTotal)}
                <span className="tooltip-count">
                  ({data.newContractsBaseCount} base{data.newContractsRevCount > 0 ? `, ${data.newContractsRevCount} rev` : ''})
                </span>
              </p>
            )}
            {(invoicedTotal > 0 || pendingInvoiceTotal > 0) && (
              <>
                <p className="tooltip-item invoiced">
                  <span className="tooltip-dot" style={{ backgroundColor: '#fbbf24' }}></span>
                  Invoiced: {formatCurrency(invoicedTotal + pendingInvoiceTotal)}
                  <span className="tooltip-count">
                    ({(data.invoicedBaseCount || 0) + (data.pendingInvoiceBaseCount || 0)} base
                    {((data.invoicedRevCount || 0) + (data.pendingInvoiceRevCount || 0)) > 0
                      ? `, ${(data.invoicedRevCount || 0) + (data.pendingInvoiceRevCount || 0)} rev`
                      : ''})
                  </span>
                </p>
                {pendingInvoiceTotal > 0 && (
                  <p className="tooltip-item pending-invoice" style={{ paddingLeft: '20px', fontSize: '12px', opacity: 0.85 }}>
                    <span className="tooltip-dot" style={{ backgroundColor: '#fde68a', width: '8px', height: '8px' }}></span>
                    Pending: {formatCurrency(pendingInvoiceTotal)}
                    <span className="tooltip-count">
                      ({data.pendingInvoiceBaseCount} base{data.pendingInvoiceRevCount > 0 ? `, ${data.pendingInvoiceRevCount} rev` : ''})
                    </span>
                  </p>
                )}
              </>
            )}
            {paidTotal > 0 && (
              <p className="tooltip-item paid">
                <span className="tooltip-dot" style={{ backgroundColor: '#34d399' }}></span>
                Paid: {formatCurrency(paidTotal)}
                <span className="tooltip-count">
                  ({data.paidBaseCount} base{data.paidRevCount > 0 ? `, ${data.paidRevCount} rev` : ''})
                </span>
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const handleMouseEnter = () => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    tooltipTimeout.current = setTimeout(() => {
      setTooltipVisible(true);
    }, 100);
  };

  const handleMouseLeave = () => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    setTooltipVisible(false);
  };

  // Custom shape that includes the value label
  const renderCustomBarShape = (props) => {
    const { fill, x, y, width, height, payload, dataKey } = props;

    if (!width || !height || width <= 0 || height <= 0) return null;

    // Check if this is a top segment
    // For Rev bars, they're always top
    // For Base bars, they're top only if there's no Rev value
    const isRevBar = dataKey?.includes('Rev');
    let isTopSegment = false;
    let stackTotal = 0;

    if (payload) {
      if (dataKey.includes('newContracts')) {
        stackTotal = (payload.newContractsBase || 0) + (payload.newContractsRev || 0);
        // Top segment if it's Rev bar, OR if it's Base bar with no Rev
        isTopSegment = isRevBar || (!isRevBar && (payload.newContractsRev || 0) === 0);
      } else if (dataKey.includes('invoiced') || dataKey.includes('pendingInvoice')) {
        // Invoiced stack includes both invoiced and pendingInvoice
        stackTotal = (payload.invoicedBase || 0) + (payload.invoicedRev || 0) +
                     (payload.pendingInvoiceBase || 0) + (payload.pendingInvoiceRev || 0);
        // Top segment logic: pendingInvoiceRev > pendingInvoiceBase > invoicedRev > invoicedBase
        const hasPendingRev = (payload.pendingInvoiceRev || 0) > 0;
        const hasPendingBase = (payload.pendingInvoiceBase || 0) > 0;
        const hasInvoicedRev = (payload.invoicedRev || 0) > 0;
        if (dataKey === 'pendingInvoiceRev') {
          isTopSegment = true;
        } else if (dataKey === 'pendingInvoiceBase') {
          isTopSegment = !hasPendingRev;
        } else if (dataKey === 'invoicedRev') {
          isTopSegment = !hasPendingRev && !hasPendingBase;
        } else if (dataKey === 'invoicedBase') {
          isTopSegment = !hasPendingRev && !hasPendingBase && !hasInvoicedRev;
        }
      } else if (dataKey.includes('paid')) {
        stackTotal = (payload.paidBase || 0) + (payload.paidRev || 0);
        isTopSegment = isRevBar || (!isRevBar && (payload.paidRev || 0) === 0);
      }
    }

    // Format the value for display
    const formattedValue = stackTotal > 0 ? Math.round(stackTotal / 1000) : 0;

    return (
      <g>
        {/* Draw the bar */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
        />
        {/* Add value label on top segments */}
        {isTopSegment && formattedValue > 0 && (
          <text
            x={x + width / 2}
            y={y - 5}
            fill={chartColors.textPrimary}
            textAnchor="middle"
            fontSize="11"
            fontWeight="500"
          >
            {`${formattedValue}k`}
          </text>
        )}
      </g>
    );
  };

  const renderBarLabel = (props) => {
    const { x, y, width, height, value, dataKey } = props;
    if (!value || value === 0 || !dataKey) return null;

    const data = props.payload;
    if (!data) return null;

    let count = 0;

    // Determine which count to show based on dataKey
    if (dataKey === 'newContractsBase') count = data.newContractsBaseCount;
    else if (dataKey === 'newContractsRev') count = data.newContractsRevCount;
    else if (dataKey === 'invoicedBase') count = data.invoicedBaseCount;
    else if (dataKey === 'invoicedRev') count = data.invoicedRevCount;
    else if (dataKey === 'pendingInvoiceBase') count = data.pendingInvoiceBaseCount;
    else if (dataKey === 'pendingInvoiceRev') count = data.pendingInvoiceRevCount;
    else if (dataKey === 'paidBase') count = data.paidBaseCount;
    else if (dataKey === 'paidRev') count = data.paidRevCount;

    if (count === 0) return null;

    // Minimum height threshold for showing label inside bar
    const minHeightForInside = 18;
    const showInside = height >= minHeightForInside;

    return (
      <text
        x={x + width / 2}
        y={showInside ? y + height / 2 + 4 : y - 4}
        fill={showInside ? "white" : chartColors.textPrimary}
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
      >
        {count}
      </text>
    );
  };

  useEffect(() => {
    return () => {
      if (tooltipTimeout.current) {
        clearTimeout(tooltipTimeout.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="manager-dashboard-container">
        <LoadingSpinner message="Loading dashboard metrics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-dashboard-container">
        <ErrorMessage 
          message={error} 
          onRetry={loadDashboardData}
          onDismiss={() => setError(null)}
        />
      </div>
    );
  }

  return (
    <div className="manager-dashboard-container">
      <div className="dashboard-header">
        <div className="header-title">
          <h1>Financial Overview</h1>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            <BarChart3 size={16} />
            Financial Metrics
          </button>
          {hasAdminAccess && (
            <button
              className={`tab-button ${activeTab === 'profit-analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('profit-analysis')}
            >
              <TrendingUp size={16} />
              Profit Analysis
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'lead-source' ? 'active' : ''}`}
            onClick={() => setActiveTab('lead-source')}
          >
            <PieChart size={16} />
            Lead Sources
          </button>
          <button
            className={`tab-button ${activeTab === 'client-revenue' ? 'active' : ''}`}
            onClick={() => setActiveTab('client-revenue')}
          >
            <Users size={16} />
            Client Revenue
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'financial' && (
        <>
          <div className="metrics-row">
            <div className="metric-card">
              <div className="metric-header">
                <FileText size={20} className="metric-icon" />
                <span className="metric-label">Backlog</span>
              </div>
              <div className="metric-value">
                {formatCurrency(backlogTotal)}
              </div>
              <div className="metric-description">
                Model price where billing status is not Paid, Voided, or Invoiced
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <DollarSign size={20} className="metric-icon" />
                <span className="metric-label">Open Invoices</span>
              </div>
              <div className="metric-value">
                {formatCurrency(openInvoicesTotal)}
              </div>
              <div className="metric-breakdown">
                <div className="breakdown-item">
                  <span className="breakdown-label">Pending</span>
                  <span className="breakdown-value">{formatCurrency(pendingInvoiceTotal)}</span>
                </div>
                <div className="breakdown-item breakdown-total">
                  <span className="breakdown-label">Total</span>
                  <span className="breakdown-value">{formatCurrency(openInvoicesTotal + pendingInvoiceTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="chart-section">
            <div className="chart-header">
              <TrendingUp size={24} className="chart-icon" />
              <h2 className="chart-title">
                {timeView.charAt(0).toUpperCase() + timeView.slice(1)} Financial Overview
              </h2>
              <div className="time-selector">
                <button 
                  className={`time-button ${timeView === 'weekly' ? 'active' : ''}`}
                  onClick={() => setTimeView('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`time-button ${timeView === 'monthly' ? 'active' : ''}`}
                  onClick={() => setTimeView('monthly')}
                >
                  Monthly
                </button>
                <button 
                  className={`time-button ${timeView === 'quarterly' ? 'active' : ''}`}
                  onClick={() => setTimeView('quarterly')}
                >
                  Quarterly
                </button>
                <button 
                  className={`time-button ${timeView === 'yearly' ? 'active' : ''}`}
                  onClick={() => setTimeView('yearly')}
                >
                  Yearly
                </button>
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={getChartData()}
                  margin={{ top: 35, right: 30, left: 20, bottom: 60 }}
                  barCategoryGap="5%"
                  barGap={0}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.borderPrimary} />
                  <XAxis
                    dataKey="period"
                    stroke={chartColors.borderPrimary}
                    tick={{ fill: chartColors.textPrimary }}
                    style={{ fontSize: '12px' }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    stroke={chartColors.borderPrimary}
                    tick={{ fill: chartColors.textPrimary }}
                    tickFormatter={value => `$${(value / 1000).toFixed(0)}k`}
                    style={{ fontSize: '12px' }}
                  />
                  <ReferenceLine
                    y={getGoalLineValue()}
                    stroke="#ef4444"
                    strokeDasharray="8 4"
                    strokeWidth={2}
                    label={{
                      value: `Goal: $${Math.round(getGoalLineValue() / 1000)}k`,
                      position: 'insideTopRight',
                      fill: '#ef4444',
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  />
                  <Tooltip 
                    content={<CustomTooltip />} 
                    cursor={{ fill: 'transparent' }}
                    isAnimationActive={false}
                    wrapperStyle={{ 
                      pointerEvents: 'none',
                      outline: 'none'
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="rect"
                    content={() => (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px', color: chartColors.textPrimary }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', fontWeight: '500' }}>
                          <span style={{ width: '12px', height: '12px', backgroundColor: '#60a5fa', display: 'inline-block', borderRadius: '2px' }}></span>
                          New Contracts
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <span style={{ width: '12px', height: '12px', backgroundColor: '#fbbf24', display: 'inline-block', borderRadius: '2px' }}></span>
                            <span style={{ width: '6px', height: '12px', backgroundColor: '#fde68a', display: 'inline-block', borderRadius: '2px' }}></span>
                          </span>
                          Invoiced
                          <span style={{ fontSize: '11px', opacity: 0.7 }}>(+ pending)</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', fontWeight: '500' }}>
                          <span style={{ width: '12px', height: '12px', backgroundColor: '#34d399', display: 'inline-block', borderRadius: '2px' }}></span>
                          Paid
                        </span>
                      </div>
                    )}
                  />
                  {/* New Contracts - Base and Rev stacked */}
                  <Bar
                    dataKey="newContractsBase"
                    stackId="newContracts"
                    fill="#60a5fa"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                  <Bar
                    dataKey="newContractsRev"
                    stackId="newContracts"
                    fill="#3b82f6"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                  
                  {/* Invoiced - Base and Rev stacked */}
                  <Bar
                    dataKey="invoicedBase"
                    stackId="invoiced"
                    fill="#fbbf24"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                  <Bar
                    dataKey="invoicedRev"
                    stackId="invoiced"
                    fill="#f59e0b"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                  {/* Pending Invoice - stacked on top of Invoiced (lighter shades) */}
                  <Bar
                    dataKey="pendingInvoiceBase"
                    stackId="invoiced"
                    fill="#fde68a"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                  <Bar
                    dataKey="pendingInvoiceRev"
                    stackId="invoiced"
                    fill="#fcd34d"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>

                  {/* Paid - Base and Rev stacked */}
                  <Bar
                    dataKey="paidBase"
                    stackId="paid"
                    fill="#34d399"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                  <Bar
                    dataKey="paidRev"
                    stackId="paid"
                    fill="#10b981"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    shape={renderCustomBarShape}
                  >
                    <LabelList content={renderBarLabel} position="center" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {activeTab === 'profit-analysis' && hasAdminAccess && (
        <div className="tab-content profit-analysis-container">
          {profitLoading ? (
            <LoadingSpinner message="Loading profit analysis data..." />
          ) : (
            <>
              {/* Filters */}
              <ProfitFilters
                filters={profitFilters}
                options={profitFilterOptions}
                onChange={handleProfitFilterChange}
                onClear={handleClearProfitFilters}
                onExport={handleProfitExport}
                projectCount={filteredProfitProjects.length}
                totalCount={profitProjects.length}
              />

              {/* Project Table */}
              <ProjectProfitTable
                projects={filteredProfitProjects}
                chartColors={chartColors}
              />
            </>
          )}
        </div>
      )}

      {activeTab === 'lead-source' && (
        <div className="tab-content">
          <LeadSourceAnalytics clients={clients} projects={projects} chartColors={chartColors} />
        </div>
      )}

      {activeTab === 'client-revenue' && (
        <div className="tab-content">
          <ClientRevenueAnalytics clients={clients} projects={projects} chartColors={chartColors} />
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;